import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Quote } from "lucide-react";
import Testimonial from "./Testimonial";

export default function TestimonialPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newReview, setNewReview] = useState({
    name: "",
    rating: 0,
    comment: "",
    image: null,
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [imageUpload, setImageUpload] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  

  const navigate = useNavigate();

  const { 
    user: auth0User, 
    isLoading: auth0Loading, 
    isAuthenticated, 
    getAccessTokenSilently 
  } = useAuth0();

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const AUTH_API = process.env.REACT_APP_AUTH0_API;

  // Famous perfume quotes
  const perfumeQuotes = [
    {
      text: "Perfume is like a new dress, it makes you quite simply marvelous.",
      author: "Estée Lauder"
    },
    {
      text: "A perfume is more than an extract; it is a presence in abstraction.",
      author: "Christian Dior"
    },
    {
      text: "A woman’s perfume tells more about her than her handwriting.",
      author: "Christian Dior"
    }
  ];

  useEffect(() => {
    if (!auth0Loading && isAuthenticated && auth0User) {
      setNewReview(prev => ({
        ...prev,
        name: auth0User.name || auth0User.email || ""
      }));
    }
  }, [auth0Loading, isAuthenticated, auth0User]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/get-all-reviews`);
        
        if (response.status === 404) {
          setTestimonials([]);
          setIsLoading(false);
          return;
        }
        
        if (!response.ok) {
          throw new Error('Failed to fetch testimonials');
        }
        
        const data = await response.json();
        
        if (data.message === "No reviews found.") {
          setTestimonials([]);
        } else if (data.reviews && Array.isArray(data.reviews)) {
          const activeTestimonials = data.reviews.filter(review => review.status === "active");
          setTestimonials(activeTestimonials);
        } else if (Array.isArray(data)) {
          const activeTestimonials = data.filter(review => review.status === "active");
          setTestimonials(activeTestimonials);
        } else {
          setTestimonials([]);
        }
      } catch (err) {
        console.error("Error fetching testimonials:", err);
        setError(err.message);
        setTestimonials([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewReview({ ...newReview, [name]: value });
  };

  const handleStarClick = (rating) => {
    setNewReview({ ...newReview, rating });
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setImageUpload(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated || !auth0User) {
      alert('Please sign in to share your experience.');
      navigate('/profile');
      return;
    }
  
    if (!newReview.name || !newReview.comment || newReview.rating <= 0) {
      alert('Please fill all required fields (name, rating, and comment)');
      return;
    }
  
    try {
      setIsSubmitting(true);
      
      if (!auth0User.email) {
        throw new Error('User email is required');
      }
      
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: AUTH_API
        }
      });
      
      const formData = new FormData();
      formData.append('name', newReview.name);
      formData.append('rating', newReview.rating.toString());
      formData.append('comment', newReview.comment);
      
      if (imageUpload) {
        formData.append('image', imageUpload);
      }
  
      const response = await fetch(`${API_BASE_URL}/create-review/${auth0User.email}/`, {
        method: 'POST',
        body: formData,
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit review');
      }
  
      const result = await response.json();
      
      const reviewToAdd = {
        name: newReview.name,
        rating: parseFloat(newReview.rating),
        comment: newReview.comment,
        image: result.imageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(newReview.name)}&background=random`,
        _id: result.reviewId || result._id,
        email: auth0User.email,
        status: "active"
      };
      
      setTestimonials([reviewToAdd, ...testimonials]);
      setNewReview({ 
        name: auth0User.name || auth0User.email || "", 
        rating: 0, 
        comment: "", 
        image: null 
      });
      setImageUpload(null);
      setImagePreview(null);
      setIsModalOpen(false);
      
    } catch (err) {
      console.error('Error submitting review:', err);
      alert(err.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReviewModal = () => {
    if (!isAuthenticated || !auth0User) {
      alert('Please sign in to share your experience.');
      navigate('/profile');
    } else {
      setIsModalOpen(true);
    }
  };

  if (auth0Loading || isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header Section */}
      <section className="pt-20 py-12 px-4 md:px-8 max-w-7xl mx-auto text-center">
        <h1 className="text-3xl md:text-5xl text-[#333333] mb-4 tracking-wide font-semibold animate-pulse">
          Voices of Elegance
        </h1>
        <p className="text-sm md:text-base text-gray-500 max-w-md mx-auto mb-6 font-light">
          Discover the stories behind MASHO Perfumes, where every scent tells a tale of luxury.
        </p>
        <div className="h-1 w-24 bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] mx-auto rounded-full transition-all duration-300 hover:scale-110"></div>
        
        <button
          onClick={openReviewModal}
          className="mt-8 bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-black px-6 py-2 rounded-sm text-sm font-medium hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
        >
          Share Your Scent Story
        </button>
      </section>

      {/* Inspirational Quotes Section */}
      <section className="py-12 px-4 md:px-8 max-w-7xl mx-auto">
        <h2 className="text-2xl md:text-3xl text-[#333333] mb-8 text-center font-medium tracking-wide">
          The Art of Perfume
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {perfumeQuotes.map((quote, index) => (
            <div
              key={index}
              className="relative bg-white rounded-lg shadow-md p-6 border border-gray-100 transition-all duration-300 hover:shadow-lg hover:border-[#D4AF37]/50"
            >
              <Quote 
                size={20} 
                className="absolute -top-2 -left-2 text-[#D4AF37] opacity-60 transition-opacity duration-300 hover:opacity-100" 
              />
              <p className="text-sm text-gray-700 italic leading-relaxed mb-4 pl-4">
                {quote.text}
              </p>
              <p className="text-xs text-[#D4AF37] font-medium text-right">
                — {quote.author}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-12 px-4 md:px-8 max-w-7xl mx-auto">
        <h2 className="text-2xl md:text-3xl text-[#333333] mb-8 text-center font-medium tracking-wide">
          Customer Experiences
        </h2>
        {testimonials.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-base text-gray-500 mb-6 font-light">
              {error ? `Error loading testimonials: ${error}` : "No testimonials yet. Be the first to share your experience!"}
            </p>
            <button
              onClick={openReviewModal}
              className="bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-black px-6 py-2 rounded-sm text-sm font-medium hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              Write a Review
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Testimonial
                key={testimonial._id || `testimonial-${index}`}
                name={testimonial.name}
                rating={testimonial.rating}
                comment={testimonial.comment}
                image={testimonial.image}
                productName={testimonial.productName}
              />
            ))}
          </div>
        )}
      </section>

      {/* Call to Action Section */}
      <section className="py-12 px-4 md:px-8 max-w-7xl mx-auto text-center bg-gradient-to-r from-[#D4AF37]/10 to-transparent rounded-lg">
        <h2 className="text-2xl md:text-4xl text-[#333333] mb-4 font-medium tracking-wide animate-glow">
          Discover Your Signature Scent
        </h2>
        <p className="text-sm md:text-base text-gray-500 max-w-md mx-auto mb-6 font-light">
          Explore our curated collection of premium fragrances crafted to elevate your essence.
        </p>
        <Link
          to="/shop"
          className="inline-block bg-gradient-to-r from-[#D4AF37] to-[#C19B2E] text-black px-6 py-3 rounded-sm text-sm font-medium hover:from-[#C19B2E] hover:to-[#B08927] transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
        >
          Shop Now
        </Link>
      </section>

      {/* Review Modal */}
      {isModalOpen && isAuthenticated && auth0User && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-5 max-w-md w-full relative border border-[#D4AF37]/20 shadow-xl">
            {/* Loading Overlay */}
            {isSubmitting && (
              <div className="absolute inset-0 bg-white bg-opacity-80 flex flex-col items-center justify-center z-10 rounded-lg">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#D4AF37] mb-3"></div>
                <p className="text-[#333333] font-medium text-sm">Submitting your review...</p>
              </div>
            )}
            
            <h2 className="text-xl font-semibold text-[#333333] mb-4 tracking-wide">
              Share Your Scent Story
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={newReview.name}
                  onChange={handleInputChange}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Rating
                </label>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className="text-xl focus:outline-none"
                      onClick={() => handleStarClick(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      disabled={isSubmitting}
                    >
                      {star <= (hoverRating || newReview.rating) ? (
                        <span className="text-[#D4AF37]">★</span>
                      ) : (
                        <span className="text-gray-300">★</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Upload Product Photo (Optional)
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full text-sm text-gray-500
                    file:mr-3 file:py-1.5 file:px-3
                    file:rounded-sm file:border-0
                    file:text-sm file:font-medium
                    file:bg-[#D4AF37] file:text-black
                    hover:file:bg-[#C19B2E]"
                  disabled={isSubmitting}
                />
                {imagePreview && (
                  <div className="mt-2">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="h-16 w-16 object-cover rounded-md border border-[#D4AF37]/30" 
                    />
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label
                  htmlFor="comment"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Your Review
                </label>
                <textarea
                  id="comment"
                  name="comment"
                  value={newReview.comment}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] text-sm"
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-200 rounded-sm hover:bg-gray-300 transition-all duration-200"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`px-3 py-1.5 text-sm font-medium text-white bg-[#D4AF37] rounded-sm transition-all duration-200 ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#C19B2E]'
                  }`}
                  disabled={isSubmitting}
                >
                  Submit Review
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
